import axios from 'axios'
import { canEditModule } from '../utils/moduleAccess'

const instance = axios.create({
  baseURL: process.env.REACT_APP_MAIN_API_ROOT,
  withCredentials: true,
})

const mutationMethods = new Set(['post', 'put', 'patch', 'delete'])

const isPublicWeddingParticipantSubmission = (
  method: string,
  url: string,
): boolean => {
  return method === 'post' && url.startsWith('/wedding/participants')
}

const getCurrentAppPath = (): string => {
  const hashPath = window.location.hash.slice(1)
  return hashPath || window.location.pathname || '/'
}

const getAuthUser = () => {
  return (
    window as Window & {
      __IVA_STORE__?: { getState: () => { auth?: { user?: any } } }
    }
  ).__IVA_STORE__?.getState().auth?.user
}

const getCurrentRouteModule = (): string | undefined => {
  const path = getCurrentAppPath()

  if (path.startsWith('/notes/to-dos')) return 'to-dos'
  if (path.startsWith('/notes/memories')) return 'memories'
  if (path.startsWith('/notes/ideas')) return 'ideas'
  if (path.startsWith('/notes/movies')) return 'movies'
  if (path.startsWith('/notes/trips')) return 'trips'
  if (path.startsWith('/notes/links')) return 'links'
  if (path.startsWith('/notes/recipes')) return 'recipes'
  if (path.startsWith('/notes/locations')) return 'locations'
  if (path.startsWith('/notes/wedding')) return 'wedding'
  if (path.startsWith('/notes/protocols')) return 'protocols'
  if (path.startsWith('/objectives') || path.startsWith('/objective/')) {
    return 'objectives'
  }
  if (path.startsWith('/events')) return 'events'
  if (path.startsWith('/translation')) return 'translation'
  if (path.startsWith('/whiteboards')) return 'whiteboards'
  if (path.startsWith('/files')) return 'files'
  if (path.startsWith('/scenarios')) return 'scenarios'
  if (path.startsWith('/purchases')) return 'purchases'
  if (path.startsWith('/settings')) return 'settings'

  return undefined
}

const getRequestModule = (url: string): string | undefined => {
  if (url.startsWith('/users/login') || url.startsWith('/users/logout')) {
    return undefined
  }

  if (url.startsWith('/users')) return 'settings'
  if (url.startsWith('/events')) return 'events'
  if (url.startsWith('/ideas')) return 'ideas'
  if (url.startsWith('/link')) return 'links'
  if (url.startsWith('/locations')) return 'locations'
  if (url.startsWith('/memories')) return 'memories'
  if (url.startsWith('/movies')) return 'movies'
  if (url.startsWith('/notes')) return 'to-dos'
  if (url.startsWith('/recipes')) return 'recipes'
  if (url.startsWith('/protocols')) return 'protocols'
  if (url.startsWith('/trips')) return 'trips'
  if (url.startsWith('/objectives') || url.startsWith('/steps')) {
    return 'objectives'
  }
  if (url.startsWith('/scenarios')) return 'scenarios'
  if (url.startsWith('/vocabularies') || url.startsWith('/words')) {
    return 'translation'
  }
  if (
    url.startsWith('/wedding-events') ||
    url.startsWith('/wedding/participants')
  ) {
    return 'wedding'
  }
  if (url.startsWith('/whiteboards')) return 'whiteboards'
  if (url.startsWith('/files/folders')) return 'files'
  if (url.startsWith('/files')) return getCurrentRouteModule() || 'files'
  if (url.startsWith('/purchases')) return 'purchases'

  return getCurrentRouteModule()
}

instance.interceptors.request.use((config) => {
  const method = config.method?.toLowerCase()

  if (!method || !mutationMethods.has(method)) {
    return config
  }

  const requestUrl = config.url || ''

  if (isPublicWeddingParticipantSubmission(method, requestUrl)) {
    return config
  }

  const moduleCode = getRequestModule(requestUrl)

  if (!moduleCode) {
    return config
  }

  const user = getAuthUser()

  if (canEditModule(user, moduleCode)) {
    return config
  }

  return Promise.reject(
    new Error('You do not have permission to edit this module.'),
  )
})

export default instance
